import { useState } from 'react'
import {
  useDeleteWelcomeItem,
  useSaveWelcomeItem,
  useUsers,
  useWelcome,
  useWelcomeItems,
  useWelcomeSuggestions
} from '../api/queries'
import type { UserRef, WelcomeItem, WelcomeTrack } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Card } from './ui'

const TRACK_TEXT: Record<WelcomeTrack, string> = {
  both: 'Begge',
  employee: 'Medarbejder',
  apprentice: 'Lærling'
}

/**
 * "Byd velkommen" til en ny kollega (docs/DECISIONS.md beslutning 118): et velkomstopslag med billedet, kontorets
 * tjekliste og den nyes egen - alt som interne opgaver med frist fra første arbejdsdag.
 */
export function WelcomeForm({ user, onClose }: { user: UserRef; onClose: (message?: string) => void }) {
  const { user: me } = useAuth()
  const { data: users } = useUsers()
  const { data: items } = useWelcomeItems()
  const welcome = useWelcome()
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [text, setText] = useState('')
  const [assignee, setAssignee] = useState('')
  const [notify, setNotify] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Medarbejder eller laerling - hver sin tjekliste (Simon). Forslaget kommer fra personens roller.
  const { data: suggestions } = useWelcomeSuggestions(true)
  const suggested = suggestions?.find((s) => s.userId === user.id)?.track ?? 'employee'
  const [chosenTrack, setChosenTrack] = useState<'employee' | 'apprentice' | null>(null)
  const track = chosenTrack ?? suggested

  const active = (items ?? []).filter((i) => i.isActive && (i.track === 'both' || i.track === track))
  const office = active.filter((i) => i.audience === 'office')
  const newcomer = active.filter((i) => i.audience === 'newcomer')

  async function send(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      const result = await welcome.mutateAsync({
        userId: user.id,
        startDate,
        text: text.trim() || null,
        officeAssigneeUserId: assignee ? Number(assignee) : null,
        notify,
        track
      })
      onClose(
        `${user.displayName} er budt velkommen: opslaget er lagt op, og ${result.officeTasks + result.newcomerTasks} opgaver er oprettet.`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Velkomsten kunne ikke sendes.')
    }
  }

  return (
    <Card title={`👋 Byd ${user.displayName} velkommen`}>
      <form className="stack" onSubmit={send}>
        <div className="field">
          <span className="field__label">Hvilken tjekliste?</span>
          <div className="row" style={{ gap: 6 }}>
            {(['employee', 'apprentice'] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={`btn btn--sm ${track === t ? 'btn--primary' : ''}`}
                onClick={() => setChosenTrack(t)}
              >
                {TRACK_TEXT[t]}
              </button>
            ))}
          </div>
          <span className="tiny muted">Foreslået ud fra rollerne. Punkterne nedenfor følger valget.</span>
        </div>

        <div className="form-grid">
          <label className="field">
            <span className="field__label">Første arbejdsdag *</span>
            <input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Kontorets punkter ligger hos</span>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Mig ({me?.displayName})</option>
              {(users ?? [])
                .filter((u) => u.isActive && !u.isHidden && u.id !== me?.id && u.id !== user.id)
                .map((u) => (
                  <option key={u.id} value={u.id}>{u.displayName}</option>
                ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span className="field__label">Et par ord i velkomstopslaget</span>
          <textarea
            rows={3}
            maxLength={4000}
            placeholder={`Tom = "Vi siger velkommen til ${user.displayName}, som starter ..." - skriv gerne, hvad han eller hun skal lave`}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <span className="tiny muted">Profilbilledet kommer med i opslaget, hvis der er et.</span>
        </label>

        <div className="small">
          <strong>Kontoret ({office.length}):</strong> {office.map((i) => i.title).join(' · ') || 'ingen punkter'}
        </div>
        <div className="small">
          <strong>{user.displayName} selv ({newcomer.length}):</strong> {newcomer.map((i) => i.title).join(' · ') || 'ingen punkter'}
        </div>

        <label className="row small" style={{ gap: 8 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          Alle får en besked om velkomstopslaget
        </label>

        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={welcome.isPending}>
            {welcome.isPending ? 'Sender ...' : 'Byd velkommen'}
          </button>
          <button className="btn" type="button" onClick={() => onClose()}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/** Velkomst-tjeklisten - kontoret retter punkterne, som kopieres ind ved hver velkomst. */
export function WelcomeChecklistEditor({ onClose }: { onClose: () => void }) {
  const { data: items } = useWelcomeItems()

  return (
    <Card title="Velkomst-tjekliste" actions={<button className="btn btn--sm" onClick={onClose}>Luk</button>}>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Punkterne bliver til interne opgaver, når nogen bydes velkommen. Fristen regnes fra første arbejdsdag.
        "Begge" gælder både medarbejdere og lærlinge. En ændring her rører ikke velkomster, der allerede er sendt.
      </div>
      {(['office', 'newcomer'] as const).map((audience) => (
        <div key={audience} className="stack" style={{ gap: 6, marginBottom: 14 }}>
          <strong className="small">{audience === 'office' ? 'Kontoret gør klar' : 'Den nye gør selv'}</strong>
          {(items ?? [])
            .filter((i) => i.audience === audience)
            .map((item) => (
              <ItemRow key={item.id} item={item} />
            ))}
          <ItemRow item={null} audience={audience} />
        </div>
      ))}
    </Card>
  )
}

function ItemRow({ item, audience }: { item: WelcomeItem | null; audience?: 'office' | 'newcomer' }) {
  const save = useSaveWelcomeItem()
  const remove = useDeleteWelcomeItem()
  const [title, setTitle] = useState(item?.title ?? '')
  const [days, setDays] = useState(String(item?.dueDaysAfterStart ?? 5))
  const [track, setTrack] = useState<WelcomeTrack>(item?.track ?? 'both')
  const [error, setError] = useState<string | null>(null)

  async function commit(nextTrack: WelcomeTrack = track) {
    setError(null)
    if (!item && !title.trim()) return
    try {
      await save.mutateAsync({
        id: item?.id,
        input: {
          title: title.trim(),
          audience: item?.audience ?? audience!,
          dueDaysAfterStart: Number(days) || 0,
          sortOrder: item?.sortOrder ?? 99,
          isActive: item?.isActive ?? true,
          track: nextTrack
        }
      })
      if (!item) {
        setTitle('')
        setDays('5')
        setTrack('both')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme.')
    }
  }

  return (
    <div>
      <div className="row" style={{ gap: 6, alignItems: 'center' }}>
        <input
          style={{ flex: 1 }}
          maxLength={200}
          placeholder={item ? undefined : '+ Nyt punkt'}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => item && title.trim() !== item.title && void commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void commit()
            }
          }}
        />
        <input
          type="number"
          min={0}
          max={90}
          title="Dage efter første arbejdsdag"
          style={{ width: 70 }}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          onBlur={() => item && Number(days) !== item.dueDaysAfterStart && void commit()}
        />
        <span className="tiny muted">dage</span>
        <select
          title="Gælder for"
          style={{ width: 'auto' }}
          value={track}
          onChange={(e) => {
            const next = e.target.value as WelcomeTrack
            setTrack(next)
            if (item) void commit(next)
          }}
        >
          {(['both', 'employee', 'apprentice'] as const).map((t) => (
            <option key={t} value={t}>{TRACK_TEXT[t]}</option>
          ))}
        </select>
        {item ? (
          <button
            className="btn btn--sm"
            type="button"
            title="Fjern punktet"
            disabled={remove.isPending}
            onClick={() => void remove.mutateAsync(item.id)}
          >
            ✕
          </button>
        ) : (
          <button className="btn btn--sm" type="button" disabled={!title.trim() || save.isPending} onClick={() => void commit()}>
            Tilføj
          </button>
        )}
      </div>
      {error && <div className="tiny warn">{error}</div>}
    </div>
  )
}
